import "@/App.css";
import { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AppNav } from "@/components/AppNav";
import { AdminUsersPage } from "@/components/AdminUsersPage";
import { AttributionLink } from "@/components/AttributionLink";
import { AuthPage } from "@/components/AuthPage";
import { Dashboard } from "@/components/Dashboard";
import { History } from "@/components/History";
import { ReflectionForm } from "@/components/ReflectionForm";
import { ReminderSettingsPage } from "@/components/ReminderSettingsPage";
import { ResourcesPage } from "@/components/ResourcesPage";
import { checkSession } from "@/lib/api";

const ProtectedLayout = ({ children }) => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const location = useLocation();

  useEffect(() => {
    let isMounted = true;

    const verifySession = async () => {
      const session = await checkSession();
      if (!isMounted) return;
      setUser(session.authenticated && session.user ? session.user : null);
      setChecking(false);
    };

    verifySession();
    return () => {
      isMounted = false;
    };
  }, []);

  const redirectState = useMemo(() => ({ from: location.pathname }), [location.pathname]);

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-[#F9F6F0] font-serif-display text-3xl text-[#1A3626]" data-testid="auth-checking-state">Preparing your reflection space…</div>;
  }

  if (!user) {
    return <Navigate to="/" replace state={redirectState} />;
  }

  return (
    <>
      <AppNav user={user} />
      {children}
      <footer className="border-t border-[#E5E1D8] bg-[#F9F6F0] px-4 py-6" data-testid="app-footer-attribution">
        <AttributionLink />
      </footer>
    </>
  );
};

const RootRoute = () => {
  const [checking, setChecking] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const verifyRootSession = async () => {
      const session = await checkSession();
      if (!isMounted) return;
      setAuthenticated(Boolean(session.authenticated && session.user));
      setChecking(false);
    };

    verifyRootSession();
    return () => {
      isMounted = false;
    };
  }, []);

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-[#F9F6F0] font-serif-display text-3xl text-[#1A3626]" data-testid="root-checking-state">Preparing your reflection space…</div>;
  }

  return authenticated ? <Navigate to="/dashboard" replace /> : <AuthPage />;
};

const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname]);

  return null;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/dashboard" element={<ProtectedLayout><Dashboard /></ProtectedLayout>} />
          <Route path="/reflect" element={<ProtectedLayout><ReflectionForm /></ProtectedLayout>} />
          <Route path="/history" element={<ProtectedLayout><History /></ProtectedLayout>} />
          <Route path="/reminders" element={<ProtectedLayout><ReminderSettingsPage /></ProtectedLayout>} />
          <Route path="/admin" element={<ProtectedLayout><AdminUsersPage /></ProtectedLayout>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
